import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { Box, Grid, Stack } from "@mui/material";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import WarningBanner from "../../../helper/WarningBanner";
import { ChartOption } from "../../../SharedComponents/Interfaces/CustomCardWithMetrics.interfaces";
import { useGetTenantACRDetailsQuery } from "../../../slices/ExploreCustomers.slice";
import { useRevenueFilters } from "./ConsumptionRevenueTab.hooks";
import { RevenueFilterableKey, RevenueFilterState } from "./ConsumptionRevenueTab.interfaces";
import { CSS_STYLES } from "./ConsumptionRevenueTab.styles";
import { ConsumptionRevenueTabCharts } from "./ConsumptionRevenueTabCharts";
import { ConsumptionRevenueTabFilterPills } from "./ConsumptionRevenueTabFilterPills";
import ConsumptionRevenueTabTable from "./ConsumptionRevenueTabTable";
import DisplayAPIsException from "../../../BasicComponents/DisplayAPIsExceptionAlert";

export default function ConsumptionRevenueTab({ tenantTopParentId }: { tenantTopParentId: string }) {
  const [params] = useSearchParams();
  const tenantId = params.get("id") || "";

  // Refs for filter selections
  const selectedFamiliesRef = useRef<string[]>([]);
  const selectedServicesRef = useRef<string[]>([]);
  const selectedProductsRef = useRef<string[]>([]);

  // State
  const [currentFilter, setCurrentFilter] = useState<RevenueFilterState>({
    filterKey: "",
    value: ""
  });
  const [filterVersion, setFilterVersion] = useState(0);

  // API Call
  const shouldFetch = Number(tenantTopParentId) > 0;

  const { data, error, isLoading } = useGetTenantACRDetailsQuery(
    { topParentId: tenantTopParentId, serviceFamily: "AllUp" },
    { skip: !shouldFetch }
  );

  // Memoized filtered items using custom hook
  const filteredItems = useRevenueFilters(
    data,
    currentFilter,
    {
      families: selectedFamiliesRef.current,
      services: selectedServicesRef.current,
      products: selectedProductsRef.current
    },
    filterVersion // add as dependency in your hook
  );

  // Handlers for filter changes
  const handleFilterChange = useCallback(
    (ref: MutableRefObject<string[]>) => (input: string) => {
      ref.current = input
        .split(";")
        .map((v) => v.trim())
        .filter(Boolean);
      setFilterVersion((v) => v + 1); // bump version to trigger update
    },
    []
  );

  // Chart data point click handler
  const handleChartDataPointClick = useCallback(
    (option: ChartOption) => {
      setCurrentFilter({
        filterKey: option.field as RevenueFilterableKey,
        value: option.value
      });
      logEvent("Consumption Revenue Tab - Chart Data Point Click", "Customer Page", "ConsumptionRevenueTab", {
        tenantId,
        chart: option.title,
        filterKey: option.field,
        filterValue: option.value
      });
    },
    [tenantId]
  );

  // Effect: Log event on mount
  useEffect(() => {
    logEvent("Consumption Revenue Tab", "Customer Page", "ConsumptionRevenueTab", { tenantId });
  }, [tenantId]);

  // Effect: Log exceptions on error
  useEffect(() => {
    if (error) {
      logException(
        "Customer Page - Consumption Revenue Tab",
        "ConsumptionRevenueTab",
        "useGetTenantACRDetailsQuery",
        error,
        {
          tenantId
        }
      );
    }
  }, [error, tenantId]);

  // Render error if present
  const renderError = () => error && !isLoading && <DisplayAPIsException error={error} height={250} />;

  return (
    <div>
      {isLoading ? (
        <Box sx={CSS_STYLES.shimmerBox}>
          <Grid container>
            <Grid item xs lg={6} xl={4} sx={{ pt: 2.5, pb: 2.5, pl: 1 }}>
              <Shimmer
                shimmerElements={[
                  { type: ShimmerElementType.line, height: 23, width: 100 },
                  { type: ShimmerElementType.gap, height: 23, width: 10 },
                  { type: ShimmerElementType.line, height: 23, width: 150 },
                  { type: ShimmerElementType.gap, height: 23, width: 10 },
                  { type: ShimmerElementType.line, height: 23, width: 150 },
                  { type: ShimmerElementType.gap, height: 23, width: 10 },
                  { type: ShimmerElementType.line, height: 23, width: 150 }
                ]}
                shimmerColors={{ shimmer: "var(--shimmer-color)" }}
                styles={CSS_STYLES.shimmerWrapper}
              />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <>
          <Stack className="filter-stack" direction="row" gap={2} sx={CSS_STYLES.filterStack}>
            <label htmlFor="viewBy">View By:</label>
            <ConsumptionRevenueTabFilterPills
              data={{ serviceFamilyGroups: data?.serviceFamilyGroups ?? [] }}
              onServiceFamiliesChange={handleFilterChange(selectedFamiliesRef)}
              onServiceNamesChange={handleFilterChange(selectedServicesRef)}
              onAcrProductNamesChange={handleFilterChange(selectedProductsRef)}
            />
          </Stack>
          <Box sx={CSS_STYLES.warningBannerContainer}>
            <WarningBanner message="This view shows ACR data at TPID level not Tenant level." />
          </Box>
        </>
      )}
      <Box sx={CSS_STYLES.chartBox}>
        <ConsumptionRevenueTabCharts
          loading={isLoading}
          onDataPointClick={handleChartDataPointClick}
          onSelectedChartFilter={setCurrentFilter}
          options={{
            chartOptions: [
              {
                field: "billingMonth",
                id: 1,
                key: "overall-revenue-trend",
                kustoLink: data?.kustoLink,
                legendName: "Overall Revenue",
                title: "Overall Revenue Trend",
                type: "line"
              },
              {
                field: "billingMonth",
                id: 2,
                key: "product-revenue-trend",
                kustoLink: data?.kustoLink,
                legendName: "Product Revenue",
                title: "Product Revenue Trend",
                type: "line"
              }
            ],
            filteredData: filteredItems,
            originalData: data?.revenueList || []
          }}
        />
      </Box>
      <Box sx={CSS_STYLES.tableBox}>
        {(!error && (
          <ConsumptionRevenueTabTable
            loading={isLoading}
            tableData={filteredItems}
            errorMessage={data?.errorMessage}
            kustoLink={data?.kustoLink}
          />
        )) ||
          ""}
        {renderError()}
      </Box>
    </div>
  );
}
